import React, { useMemo } from 'react'
import { addHours } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import {
  IntercomGetHelpCard,
  useShowIntercomGetHelp
} from './IntercomWidget/components/IntercomGetHelpCard'
import { NeedHelpPhoneCard } from './NeedHelpPhoneCard'

interface Props {
  // Whether this placement may offer the Intercom recovery flow. Only the completed-order
  // mount may: every D1 reason ("never arrived", "items missing", "arrived damaged")
  // presumes an order that was already fulfilled, and TDS-2852 mounts recovery under the
  // completed status. An in-progress order keeps the phone card, so the guest still gets
  // the restaurant's number exactly as they did before this section existed.
  //
  // Required rather than defaulted: the two mount points are only correct because they
  // disagree, and a default would let a third one silently inherit whichever behaviour
  // happened to be the default.
  allowRecoveryCard: boolean
}

export const OrderHelpSection = ({ allowRecoveryCard }: Props) => {
  const { completedOrder } = useCompletedOrder()
  const { ooRestaurant } = useRestaurant()
  const showIntercomGetHelp = useShowIntercomGetHelp()

  const orderLessThan24HoursOld = useMemo(() => {
    if (completedOrder?.promisedDateTime == null) return false
    const restaurantTimeZoneId = ooRestaurant?.timeZoneId || 'America/New_York'
    const promisedUtc = new Date(completedOrder.promisedDateTime)
    const zonedPromised = utcToZonedTime(promisedUtc, restaurantTimeZoneId)
    const zonedPlus24h = addHours(zonedPromised, 24)
    const windowEndUtc = zonedTimeToUtc(zonedPlus24h, restaurantTimeZoneId)
    return Date.now() < windowEndUtc.getTime()
  }, [completedOrder?.promisedDateTime, ooRestaurant?.timeZoneId])

  // Recovery is not limited to the 24h window that gates the legacy phone "Need help"
  // card — eligibility is decided by the refund flow itself (flag + surface + login),
  // not order age — so check it first and let it take precedence. (The 24h window still
  // gates the phone card below.) TODO(TDS-2852): if product later wants a refund time
  // limit, add it to useShowIntercomGetHelp, not to this phone-card age gate.
  if (allowRecoveryCard && showIntercomGetHelp) return <IntercomGetHelpCard />

  if (!orderLessThan24HoursOld) return null

  const i18nCountryCode = (ooRestaurant?.i18n?.country || 'US') as CountryCode
  const parsedPhoneNumber = completedOrder?.restaurant?.location?.phone
    ? parsePhoneNumber(
        completedOrder.restaurant.location.phone,
        i18nCountryCode
      )
    : null

  return parsedPhoneNumber?.isValid() ? (
    <NeedHelpPhoneCard parsedPhoneNumber={parsedPhoneNumber} />
  ) : null
}
